import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { LOGO } from "@/lib/assets";
import { ArrowLeft, Mail } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If the address exists, an email has been sent.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-icdh-navy flex items-center justify-center p-6">
      <div className="noise-overlay fixed inset-0 opacity-[0.04] pointer-events-none" />
      <div className="relative w-full max-w-md bg-icdh-white p-10 md:p-12">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src={LOGO} alt="ICDH" className="h-12 w-12 object-contain" />
          <div>
            <p className="font-extrabold tracking-tight text-base text-icdh-navy">Institution Chrétienne d'Haïti</p>
            <p className="font-serif italic text-icdh-red text-sm">{t.motto}</p>
          </div>
        </Link>
        <h1 className="font-serif text-3xl tracking-tight text-icdh-navy">Forgot password?</h1>
        <p className="mt-2 text-icdh-navy/60 text-sm">Enter your email and we'll send you a reset link.</p>

        {sent ? (
          <div data-testid="forgot-success" className="mt-8 p-6 border border-icdh-navy/15 bg-icdh-surface">
            <Mail className="text-icdh-red" />
            <p className="mt-3 font-semibold text-icdh-navy">Check your inbox.</p>
            <p className="mt-2 text-sm text-icdh-navy/70">If an account exists for <b>{email}</b>, you'll receive a link to reset your password within a few minutes. The link expires in 60 minutes.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form" className="mt-8 space-y-6">
            <input required type="email" data-testid="forgot-email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-12 border-b border-icdh-navy/25 focus:border-icdh-red outline-none bg-transparent text-icdh-navy" />
            <button type="submit" disabled={loading} data-testid="forgot-submit"
              className="w-full h-14 bg-icdh-red text-icdh-white text-xs font-bold uppercase tracking-[0.15em] hover:bg-icdh-navy transition-colors duration-300 disabled:opacity-60">
              {loading ? "..." : "Send reset link"}
            </button>
          </form>
        )}

        <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-icdh-navy/60 hover:text-icdh-red transition-colors">
          <ArrowLeft size={14} />Back to login
        </Link>
      </div>
    </div>
  );
}
